import { cx } from "@shared/components/untitledui/util/cx"
import { tracked } from "@supergrain/kernel/react"
import type { Editor } from "@tiptap/core"
import { EditorContent } from "@tiptap/react"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { UNSAFE_PortalProvider } from "react-aria"
import { createPortal } from "react-dom"
import { EditorLinkDialog } from "./editor-ui/link/EditorLinkDialog"
import { LinkHoverMenu } from "./editor-ui/link/LinkHoverMenu"
import type { LinkDialogController } from "./editor-ui/link/use-link-dialog"
import { FormattingToolbar } from "./editor-ui/toolbar/FormattingToolbar"
import { isDragMechanicsSelection } from "./extensions/drag-mechanics-selection/drag-mechanics-selection-extension"
import { SurfaceArbiter } from "./surface-arbiter"
import type { UnifiedTiptapUiValue } from "./unified-tiptap-ui-context"
import { UnifiedTiptapUiProvider } from "./UnifiedTiptapUiProvider"
import { setDragClipboard, wholeNodeOf } from "./util/clipboard"
import { classifySlice } from "./util/drag-and-drop"

interface Props {
  editor: Editor
  /** The UI context, minus the fields this component owns and supplies itself. */
  unifiedTiptapUiValue: Omit<UnifiedTiptapUiValue, "arbiter" | "setCardOverlayOpen">
  /**
   * The output of `useLinkDialog`. Created by the parent so that `linkDialog.open` can be provided to extensions.
   */
  linkDialog: LinkDialogController
  floatingPortal: HTMLElement
  /** Extra classes for the editable region, on top of the ones every editor wears. */
  className?: string
}

/**
 * Renders the given unified-tiptap editor, together with its:
 * - Formatting toolbar
 * - Link hover menu & dialog
 * - UnifiedTiptapUiProvider (for the AddButton, card menus, etc.)
 * - SurfaceArbiter
 *
 * All floating content and react-aria-components portals (including extensions')
 * are mounted within the given floatingPortal.
 *
 * The editor is assumed to include the commonExtensions from extensions.ts, and it is
 * assumed to be constant (does not change for the lifetime of this component).
 */
export default tracked(function UnifiedTiptapRenderer({
  editor,
  unifiedTiptapUiValue,
  linkDialog,
  floatingPortal,
  className,
}: Props) {
  const { canEdit } = unifiedTiptapUiValue.permissions

  // THE contextual-surface decision for this editor (see surface-arbiter.ts).
  // Boundaries report() observations into it; components listen to their own field
  // of arbiter.visible; this component publishes the drag-pad verdict to CSS.
  const arbiter = useMemo(() => new SurfaceArbiter(), [])

  // Page-level attention roots: this editor's container and its floating portal
  // (toolbar, link menu) — interacting with either counts as attention on this
  // editor; attention moving to a sibling editor suspends every surface here.
  // Refs, not state: nothing renders from either node, and a setState callback ref
  // costs every editor an extra render per node just to record it.
  const containerRef = useRef<HTMLDivElement | null>(null)
  const portalRef = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const detach = [containerRef.current, portalRef.current]
      .filter((el) => el !== null)
      .map((el) => arbiter.attachAttentionRoot(el))
    return () => detach.forEach((d) => d())
  }, [arbiter])
  // Read on demand (when an overlay portals), so it never has to change identity —
  // a fresh closure here re-renders the whole UNSAFE_PortalProvider subtree.
  const getPortalContainer = useCallback(() => portalRef.current, [])

  // One-time editor setup.
  useEffect(() => {
    // The drag boundary feeds the arbiter: re-derived after every transaction
    // (drag-mechanics selections) and whenever view.dragging flips (the setter
    // patch below — dragstart dispatches no transaction). Those are the only two
    // mutation paths, so the pushed fact never goes stale.
    const syncDragging = () =>
      arbiter.report({
        isDragging: Boolean(editor.view.dragging) || isDragMechanicsSelection(editor.state),
      })
    editor.on("transaction", syncDragging)

    // Monkey-patch editor.view.dragging to sync drag state to the shared drag clipboard.
    // This is simpler and less error-prone than overriding ProseMirror's dragstart/dragend handlers.
    let _dragging = editor.view.dragging
    Object.defineProperty(editor.view, "dragging", {
      get() {
        return _dragging
      },
      set(value: typeof _dragging) {
        _dragging = value
        if (value === null) setDragClipboard(null)
        else {
          setDragClipboard({
            sourceEditor: editor,
            sliceJSON: value.slice.toJSON(),
            sliceType: classifySlice(value.slice),
            move: value.move,
            nodeJSON: wholeNodeOf(value.slice)?.toJSON(),
          })
        }
        syncDragging()
      },
      configurable: true,
    })

    // Ensure the view is always focusable, so the toolbar can choose to work in comment-only mode.
    editor.view.dom.tabIndex = 0

    return () => {
      editor.off("transaction", syncDragging)
      // Back to the plain data property ProseMirror wrote, so a remount patches a clean view.
      Object.defineProperty(editor.view, "dragging", {
        value: _dragging,
        writable: true,
        configurable: true,
      })
    }
  }, [editor, arbiter])

  // Tracked signal reads (this component is tracked()): repaint only on flips.
  const toolbarVisible = arbiter.visible.selectionToolbar
  const dragPadsVisible = arbiter.visible.dragHandle

  // The overlay boundary: react-aria owns these overlays' state; we only read it.
  const [cardOverlayOpen, setCardOverlayOpen] = useState(false)
  useEffect(() => {
    arbiter.report({ isOverlayOpen: cardOverlayOpen || linkDialog.origin !== null })
  }, [arbiter, cardOverlayOpen, linkDialog.origin])

  const ui: UnifiedTiptapUiValue = useMemo(
    () => ({ ...unifiedTiptapUiValue, arbiter, setCardOverlayOpen }),
    [unifiedTiptapUiValue, arbiter],
  )

  return (
    // Every react-aria overlay this editor spawns (card menu, link dialog, add
    // dialogs) portals into the editor's floating portal — a registered attention
    // root — so using an overlay counts as attention on THIS editor. Overlays at
    // react-aria's default body container are no editor's territory: focus moving
    // into one nulls the page attention, un-suspending every OTHER editor's
    // surfaces. (The toolbar's own dropdowns override this again, into the toolbar
    // surface — see Toolbar.tsx.)
    <UNSAFE_PortalProvider getContainer={getPortalContainer}>
      <UnifiedTiptapUiProvider value={ui}>
        <div className="relative" ref={containerRef}>
          <EditorContent
            className={cx(
              "unified-tiptap-component unified-tiptap-html",
              // Pretend the editor is focused whenever the formatting toolbar is open,
              // even if it captures the browser focus.
              toolbarVisible && "ProseMirror-focused",
              className,
            )}
            // The drag-pad verdict, for chrome painted by plain CSS hover geometry
            // (drag pads) — it flips --chrome-reveal-opacity in unified-tiptap.css.
            data-chrome-suppressed={dragPadsVisible ? undefined : true}
            editor={editor}
          />
          <EditorLinkDialog editor={editor} origin={linkDialog.origin} onClose={linkDialog.close} />
        </div>
      </UnifiedTiptapUiProvider>
      {/* Floating content portaled to the root overlay so it appears above all editors. */}
      {createPortal(
        <div style={{ pointerEvents: "auto" }} ref={portalRef}>
          {/* <CommentSidebar editor={editor} cardStackId={cardStackId} /> */}
          {canEdit && (
            <LinkHoverMenu editor={editor} openLinkDialog={linkDialog.open} arbiter={arbiter} />
          )}
          {canEdit && (
            <FormattingToolbar editor={editor} arbiter={arbiter} openLinkDialog={linkDialog.open} />
          )}
        </div>,
        floatingPortal,
      )}
    </UNSAFE_PortalProvider>
  )
})
